import { useState, useLayoutEffect, useEffect } from 'react';
import TextInformation from '../../components/TextInformation/TextInformation';
import {
  SmallHeightDivider,
  MediumHeightDivider,
  StyledButtonOutlined,
  BodyText,
} from '../../theme/Styles';
import COLORS from '../../theme/Colors';
import { useDispatch } from 'react-redux';
import { HideGlobalLoading, ShowGlobalLoading, UpdateAppSubHeaderTitle } from '../../redux/actions/UiActions';
import {
  ButtonContainer,
  ContactInfoContainer,
  Container,
  MapContainer,
  ContactMainGrid,
  InfoBox,
  FormBox,
  SectionTitle,
  ContactCard,
  ContactLink
} from './styles/ContactStyles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import LanguageIcon from '@mui/icons-material/Language';
import { useFormik } from 'formik';
import { FormSchema, relationToData } from './ContactConstants';
import { Grid, Box, Typography, Stack } from '@mui/material';
import TextField from '../../components/TextField/TextField';
import { useQuery } from 'react-query'
import { getContactDataFromWordpress, sendMessage } from '../../api/Contact';
import PhoneTextField from '../../components/PhoneTextField/PhoneTextField';
import CenterLoading from '../../components/CenterLoading/CenterLoading';
import Select from '../../components/Select/Select';
import { useSnackbar } from 'notistack';

const toTelHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

const toWebsiteHref = (website) => {
  const value = String(website || '').trim();
  if (!value) return '#';
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

function Contact() {

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading } = useQuery(['contactData'], () => getContactDataFromWordpress())

  const formik = useFormik({
    initialValues: {
      fullName: '',
      relationTo: '',
      email: '',
      phoneNumber: '',
      message: '',
    },
    validationSchema: FormSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      handleSendMessage(values)
    },
  });

  const handleSendMessage = async () => {
    dispatch(ShowGlobalLoading(''));
    const reqData = {
      name: formik.values.fullName,
      mail: formik.values.email,
      phone: formik.values.phoneNumber,
      relation: relationToData.find((relation) => relation.value == formik.values.relationTo)?.label,
      message: formik.values.message
    }
    try {
      await sendMessage(reqData);
      enqueueSnackbar("Mensaje enviado satisfactoriamente", { variant: 'success' })
      formik.resetForm();
      dispatch(HideGlobalLoading());
    } catch (error) {
      enqueueSnackbar("Ha ocurrido un error", { variant: 'error' })
    }
  }

  useLayoutEffect(() => {
    //UPDATE APP HEADER SUBTITLE
    dispatch(UpdateAppSubHeaderTitle('Contacto')); // TITLE OF SUBHEADER APP
  }, []);

  if (isLoading) return <CenterLoading />

  return (
    <Container>
      <ContactMainGrid>
        <Grid container spacing={4}>
          {/* COLUMNA IZQUIERDA: INFORMACIÓN DE CONTACTO */}
          <Grid item xs={12} lg={6}>
            <SectionTitle>Información de contacto</SectionTitle>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '20px',
              width: '100%',
              alignItems: 'stretch'
            }}>
              {data && data.map((dept, idx) => (
                <Box key={idx} sx={{ 
                  flex: { xs: '0 0 100%', sm: '0 0 calc(50% - 10px)' },
                  minWidth: { xs: '100%', sm: '280px' },
                  display: 'flex'
                }}>
                  <ContactCard>
                    <Typography variant="h6" sx={{ color: '#202A5D', fontWeight: 700, mb: 1.5, fontSize: '1.1rem' }}>
                      {dept.title}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: '8px', mb: 1.2 }}>
                      <LocationOnIcon sx={{ color: '#94A3B8', fontSize: '20px', flexShrink: 0 }} />
                      <BodyText style={{ fontSize: '14px', lineHeight: '1.5' }}>{dept.address}</BodyText>
                    </Box>

                    <Box sx={{ display: 'flex', gap: '8px', mb: 1.2 }}>
                      <PhoneIcon sx={{ color: '#94A3B8', fontSize: '20px', flexShrink: 0 }} />
                      <ContactLink href={toTelHref(dept.phoneNumber)}>{dept.phoneNumber}</ContactLink>
                    </Box>

                    <Box sx={{ display: 'flex', gap: '8px', mb: 1.2 }}>
                      <MailIcon sx={{ color: '#94A3B8', fontSize: '20px', flexShrink: 0 }} />
                      <ContactLink href={`mailto:${dept.email}`}>{dept.email}</ContactLink>
                    </Box>

                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <LanguageIcon sx={{ color: '#94A3B8', fontSize: '20px', flexShrink: 0 }} />
                      <ContactLink href={toWebsiteHref(dept.website)} target="_blank" rel="noopener noreferrer">{dept.website}</ContactLink>
                    </Box>
                  </ContactCard>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* COLUMNA DERECHA: FORMULARIO DE CONTACTO */}
          <Grid item xs={12} lg={6}>
            <FormBox style={{ padding: '0 0 40px 0' }}>
              <SectionTitle>Contáctanos</SectionTitle>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    title='Nombre completo'
                    type='text'
                    id='fullName'
                    required
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                    helperText={formik.touched.fullName && formik.errors.fullName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Select title="Relacionado a: " id="relationTo"
                    required
                    value={formik.values.relationTo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.relationTo && Boolean(formik.errors.relationTo)}
                    helperText={formik.touched.relationTo && formik.errors.relationTo}
                    data={relationToData}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField title="Correo Electrónico"
                    type="email"
                    id="email"
                    required
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField title="Teléfono" type="text" id="phoneNumber"
                    required
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                    helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    title='Mensaje'
                    type='text'
                    id='message'
                    multiline
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.message && Boolean(formik.errors.message)}
                    helperText={formik.touched.message && formik.errors.message}
                    required
                  />
                </Grid>
              </Grid>
              <div style={{ marginTop: '30px' }}>
                <StyledButtonOutlined onClick={formik.handleSubmit} variant='outlined' style={{ width: 'auto', padding: '0 60px' }}>
                  ENVIAR MENSAJE
                </StyledButtonOutlined>
              </div>
            </FormBox>
          </Grid>
        </Grid>

        <MapContainer style={{ borderTop: 'none', marginTop: '40px', borderRadius: '12px' }}>
          <iframe
            src='https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1591.4523757607806!2d-69.9858015022195!3d18.432270509277355!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x3e1a2c31e11eb6e3!2sMinisterio%20de%20Turismo!5e0!3m2!1ses-419!2sdo!4v1638561010489!5m2!1ses-419!2sdo'
            width='100%'
            height='100%'
            style={{ border: 0 }}
            allowFullScreen=''
            loading='lazy'
            title="Ubicación Ministerio de Turismo"
          />
        </MapContainer>
      </ContactMainGrid>
    </Container>
  );
}

export default Contact;
